// Localização: frontend/src/pages/Auth/ResetPasswordPage.jsx
// Descrição: Página para redefinir senha usando token

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { verifyResetToken, resetPassword } from '../../api/api';

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    
    const navigate = useNavigate();

    // Verificar token ao montar componente
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError('Token não fornecido. Por favor, use o link enviado por email.');
                setValidatingToken(false);
                return;
            }

            try {
                const response = await verifyResetToken(token);
                if (response.data.valid) {
                    setTokenValid(true);
                } else {
                    setError('Token inválido ou expirado. Por favor, solicite um novo link de recuperação.');
                }
            } catch (err) {
                setError('Erro ao validar token. Por favor, solicite um novo link de recuperação.');
            } finally {
                setValidatingToken(false);
            }
        };

        validateToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validações
        if (password.length < 8) {
            setError('A senha deve ter no mínimo 8 caracteres.');
            return;
        }

        if (password !== confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        setLoading(true);

        try {
            await resetPassword(token, password, confirmPassword);
            setSuccess(true);
            
            // Redirecionar para login após 3 segundos
            setTimeout(() => {
                navigate('/login', { 
                    state: { 
                        message: 'Senha redefinida com sucesso! Faça login com sua nova senha.' 
                    } 
                });
            }, 3000);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 
                               err.response?.data?.error || 
                               'Erro ao redefinir senha. Tente novamente.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (validatingToken) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-fundo-cinza-moderno">
                <div className="w-full max-w-md p-10 space-y-8 bg-white rounded-2xl shadow-soft">
                    <div className="text-center">
                        <div className="flex justify-center mb-4">
                            <svg className="animate-spin h-8 w-8 text-higeia-medium" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <p className="text-gray-600">Validando token...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!tokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-fundo-cinza-moderno">
                <div className="w-full max-w-md p-10 space-y-8 bg-white rounded-2xl shadow-soft">
                    <div className="text-center space-y-4">
                        <div className="flex justify-center">
                            <img 
                                src="/images/higeia-icon-only.png"
                                alt="Higeia.tech Logo"
                                className="h-16 w-16 object-contain"
                            />
                        </div>
                        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                            Token Inválido
                        </h2>
                        {error && (
                            <div 
                                className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg" 
                                role="alert"
                            >
                                {error}
                            </div>
                        )}
                        <div className="pt-4">
                            <Link 
                                to="/forgot-password" 
                                className="font-semibold text-higeia-medium hover:text-higeia-dark transition-colors duration-200 underline-offset-2 hover:underline"
                            >
                                Solicitar novo link de recuperação
                            </Link>
                        </div>
                        <div className="pt-2">
                            <Link 
                                to="/login" 
                                className="text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                            >
                                ← Voltar para o login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-fundo-cinza-moderno">
            <div className="w-full max-w-md p-10 space-y-8 bg-white rounded-2xl shadow-soft animate-[fadeIn_0.5s_ease-out_forwards]">
                
                {/* Cabeçalho */}
                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <img 
                            src="/images/higeia-icon-only.png"
                            alt="Higeia.tech Logo"
                            className="h-16 w-16 object-contain transition-transform duration-300 hover:scale-110"
                        />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                            Redefinir Senha
                        </h2>
                        <p className="text-sm text-gray-500 font-medium">
                            Digite sua nova senha
                        </p>
                    </div>
                </div>

                {/* Formulário */}
                {!success ? (
                    <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                        
                        {error && (
                            <div 
                                className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg transition-all duration-300 ease-out animate-[slideInFromTop_0.3s_ease-out]" 
                                role="alert"
                            >
                                {error}
                            </div>
                        )}

                        <div className="space-y-1">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                Nova Senha
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                minLength={8}
                                className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-200 placeholder-gray-400 text-gray-900 
                                           bg-white transition-all duration-300 ease-in-out
                                           focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300
                                           hover:border-gray-300"
                                placeholder="Mínimo 8 caracteres"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <div className="space-y-1">
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                Confirmar Senha
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                required
                                minLength={8}
                                className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-200 placeholder-gray-400 text-gray-900 
                                           bg-white transition-all duration-300 ease-in-out
                                           focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300
                                           hover:border-gray-300"
                                placeholder="Digite a senha novamente"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <button
                            type="submit"
                            className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg 
                                      text-sm font-semibold text-white transition-all duration-300 ease-in-out
                                      ${loading 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-higeia-medium hover:bg-higeia-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
                                      }`}
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Redefinindo...
                                </span>
                            ) : (
                                'Redefinir Senha'
                            )}
                        </button>
                    </form>
                ) : (
                    <div className="mt-8 space-y-5">
                        <div 
                            className="p-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg" 
                            role="alert"
                        >
                            <p className="font-semibold mb-2">Senha redefinida com sucesso!</p>
                            <p>
                                Sua senha foi alterada. Você será redirecionado para a página de login em alguns segundos...
                            </p>
                        </div>
                    </div>
                )}

                {/* Rodapé */}
                <div className="text-center text-sm pt-4 border-t border-gray-100">
                    <Link 
                        to="/login" 
                        className="font-semibold text-higeia-medium hover:text-higeia-dark transition-colors duration-200 underline-offset-2 hover:underline"
                    >
                        ← Voltar para o login
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;

